<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <ehc-header
      :actions="actions"
      text="Organizations Management"
      @refreshOrgsAction="refreshOrgs"
      @exportOrgs="exportOrgsToCSV"
      @runArchivedOrgsDryRun="runMarkOrganizationsArchived(true)"
      @runArchivedOrgsLive="runMarkOrganizationsArchived(false)"
      @toggleShowAllOrganizations="toggleShowAllOrganizations"
    ></ehc-header>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mt-n2 mb-3"
    ></v-progress-linear>
    <v-alert
      v-if="archivedRunResult"
      :type="archivedRunResult.success ? 'success' : 'error'"
      outlined
      class="mb-4"
    >
      <div class="font-weight-bold mb-2">
        {{ archivedRunResult.title }}
      </div>
      <div v-if="archivedRunResult.message" class="mb-2">
        {{ archivedRunResult.message }}
      </div>
      <div
        v-if="archivedRunResult.summary"
        class="text-caption"
        style="white-space: pre-wrap"
      >
        {{ formatArchivedSummary(archivedRunResult.summary) }}
      </div>
    </v-alert>
    <ehc-sheet>
      <!-- Filter & Search Controls -->
      <v-card flat class="mb-4 pa-4" outlined>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="searchInput"
              placeholder="Search by name, ID, email, or creator"
              outlined
              dense
              clearable
              prepend-icon="mdi-magnify"
              @input="debouncedSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model.number="minProperties"
              label="Min Properties"
              type="number"
              outlined
              dense
              min="0"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="subscriptionFilter"
              :items="subscriptionOptions"
              label="Subscription"
              outlined
              dense
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              outlined
              dense
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" md="12">
            <v-btn-toggle v-model="dateFilter" mandatory>
              <v-btn value="all" small>All</v-btn>
              <v-btn value="1day" small>Last 1 Day</v-btn>
              <v-btn value="2days" small>Last 2 Days</v-btn>
              <v-btn value="7days" small>Last 7 Days</v-btn>
              <v-btn value="30days" small>Last 30 Days</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex gap-2">
              <v-btn
                small
                outlined
                color="primary"
                @click="resetFilters"
                :disabled="!hasActiveFilters"
              >
                <v-icon small left>mdi-close</v-icon>
                Reset Filters
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Summary Stats -->
        <v-row class="mt-2">
          <v-col cols="12">
            <div class="d-flex align-center gap-4">
              <div>
                <span class="text-overline">Total Orgs:</span>
                <span class="font-weight-bold text-h6">{{ orgs.length }}</span>
              </div>
              <div>
                <span class="text-overline">Filtered:</span>
                <span class="font-weight-bold text-h6">{{
                  filteredOrgs.length
                }}</span>
              </div>
              <div>
                <span class="text-overline">Projected Annual Income:</span>
                <span
                  class="font-weight-bold text-h6 cursor-pointer text-primary"
                  @click="openIncomeBreakdownDialog"
                  title="Click to see breakdown"
                  >${{ projectedAnnualIncome.toLocaleString() }}</span
                >
              </div>
              <v-spacer></v-spacer>
              <div class="text-caption text-grey">
                Last loaded: {{ lastLoadedTime }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Data Table -->
      <ehc-table
        :key="tableResetKey"
        :headers="tableHeaders"
        :items="filteredOrgs"
        :selectable="true"
        pagination="auto"
        :itemsPerPage="25"
        @click:row="navigateToOrg"
        :loading="loading"
      >
        <!-- Custom column renderers -->
        <template v-slot:item.subscriptionType="{ item }">
          <v-chip
            :color="getSubscriptionColor(item.subscriptionType)"
            text-color="white"
            small
          >
            {{ item.subscriptionType || "Free" }}
          </v-chip>
        </template>

        <template v-slot:item.isSubscribed="{ item }">
          <v-icon :color="item.isSubscribed === 'Active' ? 'success' : 'error'">
            {{ item.isSubscribed === "Active" ? "mdi-check" : "mdi-close" }}
          </v-icon>
          {{ item.isSubscribed }}
        </template>

        <template v-slot:item.expiresIn="{ item }">
          <span :class="getExpiresClass(item.expiresIn)">
            {{ item.expiresIn }}
          </span>
        </template>

        <template v-slot:item.propCount="{ item }">
          <v-chip size="small" variant="outlined">
            {{ item.propCount || 0 }}
          </v-chip>
        </template>
      </ehc-table>
    </ehc-sheet>

    <!-- Income Breakdown Dialog -->
    <v-dialog v-model="incomeBreakdownDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          Projected Annual Income Breakdown
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Monthly Subscriptions</v-list-item-title>
                <v-list-item-subtitle>
                  {{ incomeBreakdown.monthlyCount }} × $5 × 12 months
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold">
                  ${{ (incomeBreakdown.monthlyCount * 60).toLocaleString() }}
                </span>
              </v-list-item-action>
            </v-list-item>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Annual Subscriptions</v-list-item-title>
                <v-list-item-subtitle>
                  {{ incomeBreakdown.annualCount }} × $50 per subscription
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold">
                  ${{ (incomeBreakdown.annualCount * 50).toLocaleString() }}
                </span>
              </v-list-item-action>
            </v-list-item>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Pro Subscriptions</v-list-item-title>
                <v-list-item-subtitle>
                  {{ incomeBreakdown.proCount }} × $279 per subscription
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold">
                  ${{ (incomeBreakdown.proCount * 279).toLocaleString() }}
                </span>
              </v-list-item-action>
            </v-list-item>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Annual 10 Subscriptions</v-list-item-title>
                <v-list-item-subtitle>
                  {{ incomeBreakdown.annual10Count }} × $100 per subscription
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold">
                  ${{ (incomeBreakdown.annual10Count * 100).toLocaleString() }}
                </span>
              </v-list-item-action>
            </v-list-item>

            <v-divider class="my-3"></v-divider>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold"
                  >Total Projected Annual Income</v-list-item-title
                >
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold text-h6 text-primary">
                  ${{ projectedAnnualIncome.toLocaleString() }}
                </span>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="incomeBreakdownDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ehc-page>
</template>

<script>
import firebase from "firebase";
import mixins from "@/mixins";
import auth from "@/mixins/auth";

export default {
  mixins: [mixins, auth],
  data: function () {
    return {
      // Data
      orgs: [],
      searchInput: "",
      searchQuery: "",
      searchTimeout: null,
      minProperties: 0,
      subscriptionFilter: null,
      statusFilter: null,
      dateFilter: "all",
      showAllOrganizations: false,

      loading: false,
      orgsLastLoaded: null,

      incomeBreakdownDialog: false,
      subscriptionTypeMap: {
        trial: "Trial",
        Monthly: "Monthly",
        sub_01m: "Monthly",
        Annual: "Annual",
        sub_12m: "Annual",
        sub_100_12m: "Pro",
        Pro: "Pro",
        "Pro-10": "Pro-10",
        "PRO10-90": "PRO10-90",
        Annual10: "Annual10",
        GuestAsk: "Guest Ask",
      },
      subscriptionOptions: [
        { text: "Pro", value: "Pro" },
        { text: "Pro-10", value: "Pro-10" },
        { text: "PRO10-90", value: "PRO10-90" },
        { text: "Guest Ask", value: "GuestAsk" },
        { text: "Annual", value: "Annual" },
        { text: "Monthly", value: "Monthly" },
        { text: "Trial", value: "trial" },
        { text: "None (N/A)", value: "N/A" },
      ],
      statusOptions: [
        { text: "Active (Subscribed)", value: "active" },
        { text: "Expired (Not Subscribed)", value: "expired" },
        { text: "Expiring Soon (7 days)", value: "expiring" },
      ],

      // Options
      runningArchivedJob: false,
      archivedRunResult: null,
    };
  },
  computed: {
    actions: function () {
      const baseItems = [
        {
          type: "link",
          label: "Refresh",
          icon: "mdi-refresh",
          emitClick: "refreshOrgsAction",
          toolTip: "Reload from Firestore",
        },
        {
          type: "link",
          label: "Export to CSV",
          icon: "mdi-download",
          emitClick: "exportOrgs",
          toolTip: "Download as CSV",
        },
        {
          type: "link",
          label: this.showAllOrganizations
            ? "Show Active Only"
            : "Include Archived Organizations",
          icon: this.showAllOrganizations
            ? "mdi-filter-check-outline"
            : "mdi-filter-remove-outline",
          emitClick: "toggleShowAllOrganizations",
          toolTip: this.showAllOrganizations
            ? "Hide archived organizations"
            : "Include archived organizations",
        },
        { type: "divider" },
        {
          type: "link",
          label: "Archived Orgs Dry Run",
          icon: "mdi-play-circle-outline",
          emitClick: "runArchivedOrgsDryRun",
          toolTip: "Preview archive organization changes",
        },
        {
          type: "link",
          label: "Run Archive Orgs Now",
          icon: "mdi-calendar-check-outline",
          emitClick: "runArchivedOrgsLive",
          toolTip: "Set/clear archivedAt immediately",
        },
      ];

      return [
        {
          actionType: "menu",
          icon: "mdi-cog-outline",
          items: baseItems,
        },
      ];
    },
    tableHeaders: function () {
      return [
        {
          text: "Organization Name",
          value: "name",
          sortable: true,
          width: "15%",
        },
        {
          text: "Email",
          value: "email",
          sortable: true,
          width: "18%",
        },
        {
          text: "Type",
          value: "subscriptionType",
          sortable: true,
          width: "10%",
        },
        { text: "Status", value: "isSubscribed", sortable: true, width: "10%" },
        { text: "Expires", value: "expiresIn", sortable: true, width: "10%" },
        { text: "Created", value: "createdAt", sortable: true, width: "10%" },
        { text: "Properties", value: "propCount", sortable: true, width: "8%" },
        { text: "Org ID", value: "orgId", sortable: false, width: "19%" },
      ];
    },
    filteredOrgs: function () {
      return this.orgs
        .filter((org) => (org.propCount || 0) >= this.minProperties)
        .filter((org) => {
          if (!this.subscriptionFilter) return true;
          return org.subscriptionTypeId === this.subscriptionFilter;
        })
        .filter((org) => {
          if (!this.statusFilter) return true;
          if (this.statusFilter === "active") {
            return org.isSubscribed === "Active";
          } else if (this.statusFilter === "expired") {
            return org.isSubscribed === "Expired";
          } else if (this.statusFilter === "expiring") {
            return org.daysUntilExpire >= 0 && org.daysUntilExpire <= 7;
          }
          return true;
        })
        .filter((org) => {
          // Apply date filter
          if (this.dateFilter === "all") return true;

          if (!org.createdAtRaw) return false;

          const now = new Date();
          const orgDate = new Date(org.createdAtRaw);
          const diffTime = now - orgDate;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);

          switch (this.dateFilter) {
            case "1day":
              return diffDays <= 1;
            case "2days":
              return diffDays <= 2;
            case "7days":
              return diffDays <= 7;
            case "30days":
              return diffDays <= 30;
            default:
              return true;
          }
        })
        .filter((org) => {
          if (!this.searchQuery) return true;
          const q = this.searchQuery.toLowerCase();
          return (
            org.name?.toLowerCase().includes(q) ||
            org.orgId?.toLowerCase().includes(q) ||
            org.creator?.toLowerCase().includes(q) ||
            org.email?.toLowerCase().includes(q)
          );
        });
    },
    tableResetKey: function () {
      return [
        this.searchQuery,
        this.minProperties,
        this.subscriptionFilter,
        this.statusFilter,
        this.dateFilter,
        this.showAllOrganizations,
        this.orgs.length,
      ].join("|");
    },
    hasActiveFilters: function () {
      return (
        this.searchInput ||
        this.searchQuery ||
        this.minProperties > 0 ||
        this.subscriptionFilter ||
        this.statusFilter ||
        this.dateFilter !== "all"
      );
    },
    lastLoadedTime: function () {
      if (!this.orgsLastLoaded) return "Never";
      const now = new Date();
      const diff = now - this.orgsLastLoaded;
      const minutes = Math.floor(diff / 60000);

      if (minutes < 1) return "Just now";
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return this.orgsLastLoaded.toLocaleDateString();
    },
    projectedAnnualIncome: function () {
      const activeOrgs = this.filteredOrgs.filter(
        (org) => org.isSubscribed === "Active",
      );

      let monthlyCount = 0;
      let annualCount = 0;
      let proCount = 0;
      let annual10Count = 0;

      activeOrgs.forEach((org) => {
        // Use the raw subscription type ID to match how filtering works
        const subTypeId = org.subscriptionTypeId;
        if (subTypeId === "Monthly" || subTypeId === "sub_01m") {
          monthlyCount++;
        } else if (subTypeId === "Annual" || subTypeId === "sub_12m") {
          annualCount++;
        } else if (
          subTypeId === "Pro" ||
          subTypeId === "sub_100_12m" ||
          subTypeId === "PRO10-90" ||
          subTypeId === "Pro-10" ||
          String(subTypeId || "")
            .toLowerCase()
            .replace(/[_\s-]+/g, "")
            .includes("pro10")
        ) {
          proCount++;
        } else if (subTypeId === "Annual10") {
          annual10Count++;
        }
      });

      return (
        monthlyCount * 5 * 12 +
        annualCount * 50 +
        proCount * 279 +
        annual10Count * 100
      );
    },
    incomeBreakdown: function () {
      const activeOrgs = this.filteredOrgs.filter(
        (org) => org.isSubscribed === "Active",
      );

      let monthlyCount = 0;
      let annualCount = 0;
      let proCount = 0;
      let annual10Count = 0;

      activeOrgs.forEach((org) => {
        const subTypeId = org.subscriptionTypeId;
        if (subTypeId === "Monthly") {
          monthlyCount++;
        } else if (subTypeId === "Annual") {
          annualCount++;
        } else if (
          subTypeId === "Pro" ||
          subTypeId === "sub_100_12m" ||
          subTypeId === "PRO10-90" ||
          subTypeId === "Pro-10" ||
          String(subTypeId || "")
            .toLowerCase()
            .replace(/[_\s-]+/g, "")
            .includes("pro10")
        ) {
          proCount++;
        } else if (subTypeId === "Annual10") {
          annual10Count++;
        }
      });

      return {
        monthlyCount,
        annualCount,
        proCount,
        annual10Count,
      };
    },
  },
  methods: {
    formatArchivedSummary(summary) {
      if (!summary) return "";
      const reasons = summary.reasons || {};
      return [
        `dryRun: ${summary.dryRun}`,
        `runAt: ${summary.runAt || "n/a"}`,
        `scanned: ${summary.scanned || 0}`,
        `archived: ${summary.archived || 0}`,
        `unarchived: ${summary.unarchived || 0}`,
        `migratedLegacyInactiveAt: ${summary.migratedLegacyInactiveAt || 0}`,
        `syncedArchiveFlag: ${summary.syncedArchiveFlag || 0}`,
        `unchanged: ${summary.unchanged || 0}`,
        `errors: ${summary.errors || 0}`,
        `reasons.missing_or_invalid_expires: ${reasons.missing_or_invalid_expires || 0}`,
        `reasons.expired_12_months_or_more: ${reasons.expired_12_months_or_more || 0}`,
        `reasons.active_or_expired_less_than_12_months: ${reasons.active_or_expired_less_than_12_months || 0}`,
      ].join(" | ");
    },
    async runMarkOrganizationsArchived(dryRun = true) {
      if (this.runningArchivedJob) {
        return;
      }
      this.runningArchivedJob = true;
      this.archivedRunResult = null;
      try {
        const callable = firebase
          .functions()
          .httpsCallable("markOrganizationsArchived");
        const response = await callable({ dryRun });
        const data = response?.data || {};
        this.archivedRunResult = {
          success: true,
          title: dryRun
            ? "Archived organizations dry run complete"
            : "Archived organizations run complete",
          message: data.message || "",
          summary: data.summary || null,
        };
        this.$store.commit("setAlert", {
          type: "success",
          text: dryRun
            ? "Dry run completed for organization archiving."
            : "Organization archive job completed.",
        });
        await this.getOrganizations();
      } catch (error) {
        this.archivedRunResult = {
          success: false,
          title: "Archived organizations run failed",
          message: error?.message || "Unknown error",
          summary: null,
        };
        this.$store.commit("setAlert", {
          type: "error",
          text: `Archived organization job failed: ${error?.message || "Unknown error"}`,
        });
      } finally {
        this.runningArchivedJob = false;
      }
    },
    isOrgArchived(org) {
      return Boolean(org?.archivedAt || org?.inactiveAt);
    },
    async getOrganizations() {
      this.loading = true;

      try {
        let query = firebase.firestore().collection("organizations");
        if (!this.showAllOrganizations) {
          query = query.where("isArchived", "==", false);
        }

        const snapshot = await query.get({ source: "server" });
        let orgs = snapshot.docs.map((doc) => ({
          orgId: doc.id,
          ...doc.data(),
        }));

        if (!this.showAllOrganizations && orgs.length === 0) {
          const allSnap = await firebase
            .firestore()
            .collection("organizations")
            .get({ source: "server" });
          orgs = allSnap.docs
            .map((doc) => ({ orgId: doc.id, ...doc.data() }))
            .filter((org) => !this.isOrgArchived(org));
        }

        orgs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.orgs = this.enrichOrgData(orgs);
        this.orgsLastLoaded = new Date();
      } catch (error) {
        console.error("Error loading organizations:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text:
            "Error loading organizations: " +
            (error.message || "Unknown error"),
        });
      } finally {
        this.loading = false;
      }
    },
    enrichOrgData(orgs) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return orgs.map((org) => {
        const expiresDate = org.account?.expires
          ? new Date(org.account.expires)
          : null;

        let isSubscribed = "Expired";
        let daysUntilExpire = -1;
        let expiresIn = "Not Active";

        if (expiresDate) {
          expiresDate.setHours(0, 0, 0, 0);
          const daysLeft = Math.floor(
            (expiresDate - today) / (1000 * 60 * 60 * 24),
          );

          if (daysLeft > 0) {
            isSubscribed = "Active";
            daysUntilExpire = daysLeft;
            if (daysLeft === 0) {
              expiresIn = "Today";
            } else if (daysLeft === 1) {
              expiresIn = "Tomorrow";
            } else if (daysLeft < 30) {
              expiresIn = `${daysLeft} days`;
            } else {
              expiresIn = expiresDate.toLocaleDateString();
            }
          } else if (daysLeft === 0) {
            expiresIn = "Today";
            isSubscribed = "Expired";
          } else {
            expiresIn = `${Math.abs(daysLeft)} days ago`;
            isSubscribed = "Expired";
          }
        }

        const createdDate = org.createdAt
          ? org.createdAt.substring(0, 10)
          : "N/A";

        return {
          ...org,
          subscriptionTypeId: org.account?.subscriptionType || "N/A",
          subscriptionType:
            this.subscriptionTypeMap[org.account?.subscriptionType] ||
            org.account?.subscriptionType ||
            "N/A",
          isSubscribed,
          expiresIn,
          daysUntilExpire,
          createdAt: createdDate,
          createdAtRaw: org.createdAt,
        };
      });
    },
    getSubscriptionColor(subType) {
      const colors = {
        Pro: "#667eea",
        "Pro-10": "#667eea",
        "Guest Ask": "#11998e",
        GuestAsk: "#11998e",
        Standard: "#764ba2",
        Basic: "#f093fb",
        Free: "#999999",
      };
      return colors[subType] || "#999999";
    },
    getExpiresClass(expiresIn) {
      if (expiresIn.includes("ago")) return "text-error font-weight-bold";
      if (expiresIn.includes("Today")) return "text-warning font-weight-bold";
      if (expiresIn.includes("Tomorrow")) return "text-warning";
      return "text-success";
    },
    toggleShowAllOrganizations() {
      this.showAllOrganizations = !this.showAllOrganizations;
      this.getOrganizations();
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchQuery = this.searchInput || "";
      }, 300);
    },
    resetFilters() {
      clearTimeout(this.searchTimeout);
      this.searchInput = "";
      this.searchQuery = "";
      this.minProperties = 0;
      this.subscriptionFilter = null;
      this.statusFilter = null;
      this.dateFilter = "all";
    },
    navigateToOrg(org) {
      this.$router.push(`/organization/${org.orgId}`);
    },
    refreshOrgs() {
      this.getOrganizations();
    },
    exportOrgsToCSV() {
      const headers = [
        "Name",
        "Subscription Type",
        "Status",
        "Expires",
        "Properties",
        "Creator",
        "Org ID",
      ];
      const rows = this.filteredOrgs.map((org) => [
        org.name || "",
        org.subscriptionType || "",
        org.isSubscribed || "",
        org.expiresIn || "",
        org.propCount || 0,
        org.creator || "",
        org.orgId || "",
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `organizations-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.$forceUpdate(); // Refresh to show feedback
    },
    openIncomeBreakdownDialog() {
      this.incomeBreakdownDialog = true;
    },
  },
  created() {
    this.getOrganizations();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.text-overline {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
}
</style>
